import {Form, Input, Select, Button} from 'antd';
import {MinusCircleOutlined, PlusOutlined} from '@ant-design/icons';
import {useMutation} from '@apollo/client';
import {UPDATE_MINI_FEEDBACK_QUESTION} from '../../resolvers';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {Button as DabaButton} from '@dabafinance/react-components';

const QUESTION_TYPES = [
  {label: 'Multiple Select', value: 'OPTION'},
  {label: 'Single Select', value: 'RADIO'},
  {label: 'Text', value: 'TEXT'},
  {label: 'Number', value: 'NUMBER'},
  {label: 'Datetime', value: 'DATETIME'},
  {label: 'File', value: 'FILE'},
];

const getMultilangFromQuestion = (question, translationLang) => {
  const ml = question?.multilang || [];
  const translated = ml.find(m => m?.language === translationLang);
  return {
    questionText: question?.questionText || '',
    questionTextTranslated: translated?.questionText || '',
    options: (question?.options || []).map((opt, i) => ({
      label: opt?.label || '',
      labelTranslated: translated?.options?.[i]?.label || '',
    })),
  };
};

const buildMultilangArray = (q, options, translationLang) => {
  const translatedQuestionText = q?.multilang?.[translationLang]?.questionText;
  const hasTranslatedOptions =
    ['RADIO', 'OPTION', 'DROPDOWN'].includes(q?.questionType) &&
    (options || []).some(opt => opt?.labelTranslated?.trim());
  if (!translatedQuestionText?.trim() && !hasTranslatedOptions) return null;
  const translatedItem = {
    language: translationLang,
    questionText: translatedQuestionText?.trim() || null,
  };
  if (hasTranslatedOptions) {
    translatedItem.options = (options || [])
      .filter(opt => opt?.label != null)
      .map(opt => ({
        label: opt.labelTranslated?.trim() || opt.label,
        value: opt.label
          ? opt.label.trim().toUpperCase().replace(/\s+/g, '_')
          : '',
      }));
  }
  return [translatedItem];
};

export const EditQuestionForm = ({
  feedbackId,
  question,
  allQuestions,
  closeModal,
  refetch,
  baseLanguage = 'EN',
}) => {
  const [updateQuestions, {loading}] = useMutation(
    UPDATE_MINI_FEEDBACK_QUESTION,
  );
  const [translateText, {loading: loadingTranslation}] =
    useMutation(TRANSLATE_TEXT);
  const [form] = Form.useForm();
  const normalizedBaseLanguage = baseLanguage === 'FR' ? 'FR' : 'EN';
  const translationLang = normalizedBaseLanguage === 'EN' ? 'FR' : 'EN';
  const baseLanguageLabel =
    normalizedBaseLanguage === 'EN' ? 'English' : 'French';
  const translationLanguageLabel =
    normalizedBaseLanguage === 'EN' ? 'French' : 'English';

  const handleTranslateQuestion = async () => {
    const sourceText = form.getFieldValue('questionText');
    if (!sourceText?.trim()) {
      toast.error(`Please enter question text in ${baseLanguageLabel} first`);
      return;
    }
    try {
      const {data} = await translateText({
        variables: {
          sourceLang: normalizedBaseLanguage,
          targetLang: translationLang,
          sourceText,
        },
      });
      if (
        data?.translateText?.statusCode === 400 ||
        data?.translateText?.__typename === 'Error'
      ) {
        toast.error(data?.translateText?.message || 'Translation failed');
      } else {
        const translated = data?.translateText?.targetText || '';
        form.setFieldValue(
          ['multilang', translationLang, 'questionText'],
          translated,
        );
        toast.success('Translated successfully');
      }
    } catch (err) {
      toast.error(err?.message || 'Translation failed');
    }
  };

  const handleTranslateOptions = async optionIndex => {
    const options = form.getFieldValue('options') || [];
    const opt = options[optionIndex];
    if (!opt?.label?.trim()) {
      toast.error(
        `Please enter the option label in ${baseLanguageLabel} first`,
      );
      return;
    }
    try {
      const {data} = await translateText({
        variables: {
          sourceLang: normalizedBaseLanguage,
          targetLang: translationLang,
          sourceText: opt.label,
        },
      });
      if (
        data?.translateText?.statusCode === 400 ||
        data?.translateText?.__typename === 'Error'
      ) {
        toast.error(data?.translateText?.message || 'Translation failed');
      } else {
        const translated = data?.translateText?.targetText || '';
        const newOptions = [...options];
        newOptions[optionIndex] = {
          ...newOptions[optionIndex],
          labelTranslated: translated,
        };
        form.setFieldValue('options', newOptions);
        toast.success('Translated successfully');
      }
    } catch (err) {
      toast.error(err?.message || 'Translation failed');
    }
  };

  const onFinish = values => {
    const updatedPayload = allQuestions.map(q => {
      if (q.id !== question.id) {
        const existingTranslation = q.multilang?.find(
          m => m?.language === translationLang,
        );
        const hasMultilang =
          existingTranslation?.questionText?.trim() ||
          existingTranslation?.options?.length;
        return {
          id: q.id,
          questionText: q.questionText,
          questionType: q.questionType,
          order: q.order,
          options: q.options
            ? q.options.map(opt => ({label: opt.label, value: opt.value}))
            : [],
          multilang: hasMultilang
            ? [
                {
                  language: existingTranslation.language || translationLang,
                  questionText:
                    existingTranslation.questionText?.trim() || null,
                  ...(existingTranslation.options?.length
                    ? {
                        options: existingTranslation.options.map(o => ({
                          label: o.label,
                          value: o.value,
                        })),
                      }
                    : {}),
                },
              ]
            : null,
        };
      }

      const updatedOpts =
        ['RADIO', 'OPTION'].includes(values.questionType) && values.options
          ? values.options.map(opt => ({
              label: opt.label,
              value: opt.label
                ? opt.label.trim().toUpperCase().replace(/\s+/g, '_')
                : '',
            }))
          : null;

      const payload = {
        id: q.id,
        questionText: values.questionText,
        questionType: values.questionType,
        order: q.order,
        options: updatedOpts,
      };

      const multilang = buildMultilangArray(
        {
          multilang: {
            [translationLang]: {
              questionText: values.multilang?.[translationLang]?.questionText,
            },
          },
          questionType: values.questionType,
        },
        values.options,
        translationLang,
      );
      if (multilang?.length) {
        payload.multilang = multilang;
      }

      return payload;
    });

    updateQuestions({
      variables: {
        input: {
          feedbackId,
          questions: updatedPayload,
        },
      },
    })
      .then(({data}) => {
        if (data?.updateMiniFeedbackQuestions?.__typename === 'Error') {
          toast.error(data.updateMiniFeedbackQuestions.message);
        } else {
          toast.success(
            data?.updateMiniFeedbackQuestions?.message ||
              'Question updated successfully!',
          );
          form.resetFields();
          refetch();
          closeModal();
        }
      })
      .catch(err => {
        toast.error(err.message || 'An error occurred during update');
      });
  };

  const initial = getMultilangFromQuestion(question, translationLang);

  return (
    <Form
      form={form}
      layout="vertical"
      style={{maxHeight: '70vh', overflowY: 'auto', paddingRight: '10px'}}
      onFinish={onFinish}
      initialValues={{
        questionText: question?.questionText,
        questionType: question?.questionType,
        options: initial.options.length
          ? initial.options
          : (question?.options || []).map(opt => ({
              label: opt?.label || '',
              labelTranslated: '',
            })),
        multilang: {
          [translationLang]: {questionText: initial.questionTextTranslated},
        },
      }}>
      <h1 style={{fontSize: 24, marginBottom: 20}}>Edit Question</h1>

      <div
        style={{
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          padding: '24px',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          marginBottom: '24px',
        }}>
        <div style={{display: 'flex', gap: 16, alignItems: 'flex-start'}}>
          <Form.Item
            name="questionText"
            label={`Question Text (${normalizedBaseLanguage})`}
            rules={[{required: true, message: 'Question text is required'}]}
            style={{flex: 1}}>
            <Input
              placeholder={`Enter question text in ${baseLanguageLabel}`}
            />
          </Form.Item>
          <div style={{paddingTop: 30}}>
            <DabaButton
              type="secondary"
              label={loadingTranslation ? 'Translating...' : 'Translate'}
              disabled={loadingTranslation}
              onClick={handleTranslateQuestion}
            />
          </div>
        </div>

        <Form.Item
          name={['multilang', translationLang, 'questionText']}
          label={`Question Text (${translationLang})`}>
          <Input
            placeholder={`Enter or translate to ${translationLanguageLabel}`}
          />
        </Form.Item>

        <Form.Item
          name="questionType"
          label="Question Type"
          rules={[{required: true, message: 'Question type is required'}]}>
          <Select placeholder="Select a question type">
            {QUESTION_TYPES.map(type => (
              <Select.Option key={type.value} value={type.value}>
                {type.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.questionType !== currentValues.questionType
          }>
          {() => {
            const type = form.getFieldValue('questionType');
            return ['RADIO', 'OPTION'].includes(type) ? (
              <div style={{marginLeft: 20}}>
                <p style={{marginBottom: 8}}>Options</p>
                <Form.List
                  name="options"
                  rules={[
                    {
                      validator: async (_, options) => {
                        if (!options || options.length < 1) {
                          return global.Promise.reject(
                            new Error('At least one option must be added'),
                          );
                        }
                      },
                    },
                  ]}>
                  {(
                    optionFields,
                    {add: addOption, remove: removeOption},
                    {errors},
                  ) => (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 12,
                          marginBottom: 16,
                        }}>
                        {optionFields.map((optionField, oIdx) => (
                          <div
                            key={optionField.key}
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 8,
                              alignItems: 'flex-end',
                            }}>
                            <Form.Item
                              {...optionField}
                              label={`Label (${normalizedBaseLanguage})`}
                              style={{
                                minWidth: 180,
                                margin: 0,
                                flex: 1,
                              }}
                              name={[optionField.name, 'label']}
                              rules={[
                                {required: true, message: 'Missing label'},
                              ]}>
                              <Input
                                placeholder={`Enter option in ${baseLanguageLabel}`}
                              />
                            </Form.Item>
                            <Form.Item
                              {...optionField}
                              label={`Label (${translationLang})`}
                              style={{
                                minWidth: 180,
                                margin: 0,
                                flex: 1,
                              }}
                              name={[optionField.name, 'labelTranslated']}>
                              <Input
                                placeholder={`Enter option in ${translationLanguageLabel}`}
                              />
                            </Form.Item>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                height: 32,
                              }}>
                              <DabaButton
                                type="secondary"
                                label="Translate"
                                disabled={loadingTranslation}
                                onClick={() => handleTranslateOptions(oIdx)}
                              />
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                height: 32,
                              }}>
                              <MinusCircleOutlined
                                style={{
                                  color: '#ef4444',
                                  fontSize: 18,
                                  cursor: 'pointer',
                                }}
                                onClick={() => removeOption(optionField.name)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => addOption()}
                          block
                          style={{
                            borderColor: '#8b5cf6',
                            color: '#8b5cf6',
                          }}
                          icon={<PlusOutlined />}>
                          Add Option
                        </Button>
                        <Form.ErrorList errors={errors} />
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </div>
            ) : null;
          }}
        </Form.Item>
      </div>

      <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 30}}>
        <SubmitButton
          label="Save Changes"
          loading={loading}
          disabled={loading}
        />
      </div>
    </Form>
  );
};
